// ---------------------------------------------------------------------------
// Foto parada de avatar Lottie — o que as listas mostram no lugar do player.
//
// ## Por que existe (BUG-197, gatilho medido em 25/09/2026)
//
// Um avatar Lottie de AMIGO no "Amigos lendo agora" da Home travava a pintura
// do WebKit do iPhone na volta do fundo: Home → bloquear → voltar → rolar =
// seções inteiras sem pintura, até matar o app. Reproduzido no Safari do
// iPhone (sem WebView do app, sem sonda nativa) e fechado por A/B nos dois
// sentidos pelo fundador: avatar do amigo trocado por foto comum → parou;
// Lottie de volta → travou de novo. É o "é ESTADO, não componente" da 6ª
// fatia: o avatar é dado de OUTRA conta e só aparece na Home de quem é amigo.
//
// O mecanismo DENTRO do player não está medido (o loop de rAF só roda
// tocando, e no carrossel ele não toca). O que está medido é que um
// `<canvas>` do player vivo na página é o gatilho. Então fora do perfil da
// própria pessoa nenhum canvas fica na página: o player desenha o frame 0
// num canvas FORA do documento, vira `data:` PNG e é destruído.
//
// ## Três decisões que não são estilo
//
//  1. **Um player por vez, e um por URL por carga.** O mesmo amigo aparece
//     empilhado no cabeçalho E no card; sem dedupe seriam N núcleos WASM
//     simultâneos. A fila serializa URLs diferentes.
//  2. **Falha também fica em cache** (`null`): sem isso, uma URL morta (a
//     cópia antiga que o `setProfileImage` destruiu no Cloudinary) seria
//     retentada a cada montagem de avatar.
//  3. **Teto de tempo.** Uma geração que nunca resolve seguraria a fila e,
//     com ela, todo avatar Lottie da tela — vira `null` e o monograma fica.
// ---------------------------------------------------------------------------

/** Lado do bitmap gerado. Cobre o maior avatar de lista (~96px) × DPR 2. */
export const LOTTIE_STILL_PX = 256;

/** Teto de uma geração (download do arquivo + WASM + 1 frame). */
export const LOTTIE_STILL_TIMEOUT_MS = 10_000;

/** Gera a foto de UMA URL. `null` = não deu (a tela mostra o monograma). */
export type RenderLottieStill = (src: string) => Promise<string | null>;

export interface LottieStillCache {
  /** A foto já pronta, sem esperar — para a primeira renderização. */
  peek(src: string): string | null | undefined;
  /** A foto, gerando se preciso. Chamadas repetidas dividem a mesma promessa. */
  get(src: string): Promise<string | null>;
}

export function createLottieStillCache(render: RenderLottieStill): LottieStillCache {
  const settled = new Map<string, string | null>();
  const inFlight = new Map<string, Promise<string | null>>();
  let queue: Promise<unknown> = Promise.resolve();

  return {
    peek(src) {
      return settled.has(src) ? settled.get(src) : undefined;
    },
    get(src) {
      if (settled.has(src)) return Promise.resolve(settled.get(src) ?? null);
      const pending = inFlight.get(src);
      if (pending) return pending;

      const run = queue.then(async () => {
        let still: string | null = null;
        try {
          still = await render(src);
        } catch {
          still = null;
        }
        settled.set(src, still);
        inFlight.delete(src);
        return still;
      });
      // A fila segue mesmo se uma geração rejeitar (o `try` acima já engole,
      // mas a cauda não pode depender disso).
      queue = run.catch(() => undefined);
      inFlight.set(src, run);
      return run;
    },
  };
}

/** O mínimo do `DotLottie` que a geração usa — injetável no teste. */
export interface StillPlayer {
  addEventListener(type: "load" | "loadError", listener: () => void): void;
  destroy(): void;
}

export type CreateStillPlayer = (canvas: HTMLCanvasElement, src: string) => StillPlayer;

/**
 * Desenha o frame 0 num canvas que NUNCA entra no documento e devolve o PNG.
 *
 * O `load` do dotlottie-web é despachado num `setTimeout(0)` depois de o
 * núcleo já ter renderizado e desenhado o frame no canvas (medido na fonte
 * 0.72.1), então no `load` o pixel está lá. Com o canvas fora do documento o
 * `resize()` do player lê um retângulo 0×0 e mantém o tamanho dado aqui, e o
 * IntersectionObserver nunca o vê — nada dele fica vivo depois do `destroy`.
 */
export function renderLottieStillWith(
  createPlayer: CreateStillPlayer,
  createCanvas: () => HTMLCanvasElement = () => document.createElement("canvas"),
  timeoutMs: number = LOTTIE_STILL_TIMEOUT_MS,
): RenderLottieStill {
  return (src) =>
    new Promise<string | null>((resolve) => {
      const canvas = createCanvas();
      canvas.width = LOTTIE_STILL_PX;
      canvas.height = LOTTIE_STILL_PX;

      let player: StillPlayer | null = null;
      let done = false;
      const finish = (still: string | null) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        try {
          player?.destroy();
        } catch {
          // Destruir é faxina; falhar nela não muda o resultado.
        }
        resolve(still);
      };
      const timer = setTimeout(() => finish(null), timeoutMs);

      try {
        player = createPlayer(canvas, src);
      } catch {
        finish(null);
        return;
      }
      player.addEventListener("load", () => {
        try {
          finish(canvas.toDataURL("image/png"));
        } catch {
          finish(null);
        }
      });
      player.addEventListener("loadError", () => finish(null));
    });
}

/**
 * Produção: o `DotLottie` do motor do player, carregado sob demanda.
 *
 * ⚠️ Do `dotlottie-web`, NÃO do `dotlottie-react`: os tipos do wrapper dizem
 * `export * from "@lottiefiles/dotlottie-web"`, mas em runtime o módulo só
 * expõe `DotLottieReact`, `DotLottieWorkerReact` e `setWasmUrl` (medido no
 * Vite em 25/09) — o construtor chegava `undefined` e toda foto dava `null`.
 */
const renderWithDotLottie: RenderLottieStill = async (src) => {
  const { DotLottie } = await import("@lottiefiles/dotlottie-web");
  return renderLottieStillWith(
    (canvas, url) =>
      new DotLottie({
        canvas,
        src: url,
        autoplay: false,
        loop: false,
        renderConfig: { autoResize: false, freezeOnOffscreen: false, devicePixelRatio: 1 },
      }),
  )(src);
};

export const lottieStillCache: LottieStillCache = createLottieStillCache(renderWithDotLottie);
